import { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import classNames from 'classnames';
import Icon from './Icon';
import { getUniqueId, translate } from '@micro-apps/env';
import { fuzzy } from '@micro-apps/shared';
import classnames from 'classnames';

type dataItem = { node: React.ReactNode; search: string };
export type Props = {
	id?: string;
	initialValue?: string;
	data?: dataItem[];
	extract?: (i: dataItem) => unknown;
	disable?: (i: dataItem) => boolean;
	filter?: (i: dataItem) => boolean;
	noResults?: React.ReactNode;
	defaultResults?: React.ReactNode[];
	inputClassName?: string;
	placeholder?: string;
	onSelect?: (result: unknown) => unknown;
	setValueOnCLick?: boolean;
};

export default function Typeahead({
	id = 'typeahead-' + getUniqueId(),
	initialValue = '',
	data = [],
	extract = item => item.search,
	disable = item => false,
	filter = item => false,
	noResults = 'No results',
	inputClassName,
	placeholder,
	defaultResults = [],
	setValueOnCLick,
}: PropsWithChildren<Props>): ReactElement {
	const [value, setValue] = useState(initialValue);
	const [selectedIndex, setSelectedIndex] = useState(-1);
	const [isSelected, setIsSelected] = useState(false);
	const defaultResultsArr = useMemo(() => {
		return defaultResults
			? defaultResults.map(node => {
					return {
						disabled: false,
						original: {
							node,
						},
					};
				})
			: [];
	}, [defaultResults]);

	useEffect(() => {
		setSelectedIndex(-1);
	}, [defaultResults]);

	const results = useMemo(() => {
		const options = { pre: '<mark>', post: '</mark>', extract };

		return value
			? fuzzy
					.filter(value, data, options)
					.filter(({ score }) => !value || score > 0)
					.filter(result => !filter(result.original))
					.map(result => ({ ...result, disabled: disable(result.original) }))
			: defaultResultsArr;
	}, [data, defaultResultsArr, disable, extract, filter, value]);
	const select = () => {
		const result = results[selectedIndex];
		if (!result) {
			return;
		}
		if (result.disabled) return;

		let selectedValue = extract(result.original);
		selectedValue = Array.isArray(selectedValue) ? selectedValue[0] : selectedValue;

		setValue(selectedValue as string);
	};

	function change(direction) {
		let index =
			direction > 0 && selectedIndex === results.length - 1 ? 0 : selectedIndex + direction;
		if (index < 0) index = results.length - 1;

		let disabled = results[index].disabled;

		while (disabled) {
			if (index === results.length) {
				index = 0;
			} else {
				index += direction;
			}

			disabled = results[index].disabled;
		}

		setSelectedIndex(index);
	}

	useEffect(() => {
		const onFocus = (e: FocusEvent) => {
			const target = e.target as HTMLButtonElement;
			const data = target.dataset;
			if (data && data['index']) {
				try {
					const i = Number.parseInt(data['index']);
					setSelectedIndex(i);
				} catch {
					//empty by design
				}
			}
		};
		window.addEventListener('focusin', onFocus);

		return () => {
			window.removeEventListener('focusin', onFocus);
		};
	}, []);

	return (
		<div
			className={classnames('ReactTypeahead', { dropdown: results.length > 0 })}
			role="combobox"
			aria-haspopup="listbox"
			aria-owns={`${id}-listbox`}
			aria-expanded={results.length > 0}
			aria-controls={`${id}-listbox`}
			id={`${id}-typeahead`}
		>
			<div className="ReactTypeahead-input">
				<input
					className={inputClassName}
					id={id}
					placeholder={placeholder}
					aria-autocomplete="list"
					aria-controls={`${id}-listbox`}
					aria-labelledby={`${id}-label`}
					aria-activedescendant={
						selectedIndex >= 0 && results.length > 0 ? `${id}-result-${selectedIndex}` : undefined
					}
					onInput={e => {
						isSelected && setIsSelected(false);
						setValue((e.target as HTMLInputElement).value);
					}}
					value={value}
					autoComplete="off"
					spellCheck="false"
					onKeyDown={e => {
						if (results.length === 0) return;
						switch (e.key) {
							case 'Enter':
								select();
								break;
							case 'ArrowDown':
								e.preventDefault();
								change(1);
								break;
							case 'ArrowUp':
								e.preventDefault();
								change(-1);
								break;
							case 'Escape':
								e.preventDefault();
								break;
						}
					}}
				/>
				{value ? (
					<button
						onClick={() => {
							setValue('');
							setIsSelected(false);
						}}
						aria-label={translate('Clear')}
						className="icon icon-close"
						title={translate('Clear')}
					/>
				) : (
					<Icon icon="search"></Icon>
				)}
			</div>

			<ul
				className="ReactTypeahead-list"
				role="listbox"
				aria-labelledby={`${id}-label`}
				id={`${id}-listbox`}
			>
				{results.length > 0 && !isSelected ? (
					results.map((result, i) => (
						<li
							key={i}
							role="option"
							id={`${id}-result-${i}`}
							className={classNames('ReactTypeahead-item', {
								selected: selectedIndex === i,
								disabled: result.disabled,
							})}
							aria-selected={selectedIndex === i}
							onClick={() => {
								if (setValueOnCLick) {
									setValue(result.original.search as string);
									setIsSelected(true);
								} else if (!result.disabled) {
									setSelectedIndex(i);
								}
							}}
						>
							{result.original.node}
						</li>
					))
				) : (
					<div className="no-results">{noResults} </div>
				)}
			</ul>
		</div>
	);
}
